import { useState, useCallback, useEffect } from 'react';
import type { Message } from '../types';
import {
  loadSessionMetadata,
  saveSessionMetadata,
  loadSession,
  saveSession,
  deleteSession,
  createSessionId,
  setCurrentSessionId,
  getCurrentSessionId,
  deriveSessionTitle,
  deriveSessionPreview,
  type SessionMetadata,
} from '../utils/sessionHistory';
import { sanitizeMessagesForStorage } from '../utils/messageStorage';

interface UseSessionHistoryOptions {
  active: boolean;
  maxSessions: number;
  setMessages: (msgs: Message[]) => void;
  chatDispatch: (action: { type: 'SET_INPUT'; input: string }) => void;
  setThreadId: (id: string | null) => void;
  uiDispatch: (action: { type: 'CLEAR_ATTACHMENTS' } | { type: 'CLEAR_ATTACHMENT_ERRORS' }) => void;
  messages: Message[];
  threadId: string | null;
}

export function useSessionHistory({
  active,
  maxSessions,
  setMessages,
  chatDispatch,
  setThreadId,
  uiDispatch,
  messages,
  threadId,
}: UseSessionHistoryOptions) {
  const [sessionsOpen, setSessionsOpen] = useState(false);
  const [sessions, setSessions] = useState<SessionMetadata[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  const applySessionLimit = useCallback(
    (list: SessionMetadata[]) => {
      if (!maxSessions || maxSessions <= 0) return { list, removed: [] as string[] };
      if (list.length <= maxSessions) return { list, removed: [] as string[] };
      const trimmed = list.slice(0, maxSessions);
      const removed = list.slice(maxSessions).map(meta => meta.id);
      return { list: trimmed, removed };
    },
    [maxSessions]
  );

  const createNewSession = useCallback(
    (existing?: SessionMetadata[]) => {
      const now = new Date().toISOString();
      const id = createSessionId();
      const title = 'New conversation';
      const sessionData = {
        id,
        title,
        createdAt: now,
        lastModified: now,
        messages: [] as Message[],
        threadId: null,
      };
      const metadata: SessionMetadata = {
        id,
        title,
        createdAt: now,
        lastModified: now,
        messageCount: 0,
        preview: '',
      };

      saveSession(sessionData);
      const baseList = existing ?? loadSessionMetadata();
      const merged = [metadata, ...baseList.filter(meta => meta.id !== id)].sort((a, b) =>
        b.lastModified.localeCompare(a.lastModified)
      );
      const { list, removed } = applySessionLimit(merged);
      saveSessionMetadata(list);
      removed.forEach(deleteSession);
      setSessions(list);
      setActiveSessionId(id);
      setCurrentSessionId(id);
      setMessages([]);
      chatDispatch({ type: 'SET_INPUT', input: '' });
      setThreadId(null);
      uiDispatch({ type: 'CLEAR_ATTACHMENTS' });
      uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
      setSessionsOpen(false);
      return id;
    },

    [applySessionLimit, setMessages, chatDispatch, setThreadId, uiDispatch]
  );

  const selectSession = useCallback(
    (id: string) => {
      const data = loadSession(id);
      if (!data) return;
      setActiveSessionId(id);
      setCurrentSessionId(id);
      setMessages(data.messages || []);
      chatDispatch({ type: 'SET_INPUT', input: '' });
      setThreadId(data.threadId ?? null);
      uiDispatch({ type: 'CLEAR_ATTACHMENTS' });
      uiDispatch({ type: 'CLEAR_ATTACHMENT_ERRORS' });
      setSessionsOpen(false);
    },

    [setMessages, chatDispatch, setThreadId, uiDispatch]
  );

  const removeSession = useCallback(
    (id: string) => {
      deleteSession(id);
      const updated = loadSessionMetadata().filter(meta => meta.id !== id);
      saveSessionMetadata(updated);
      setSessions(updated);
      if (id === activeSessionId) {
        createNewSession(updated);
      }
    },
    [activeSessionId, createNewSession]
  );

  // Initialize: load session list and restore active session
  useEffect(() => {
    if (!active) return;
    const metadata = loadSessionMetadata();
    setSessions(metadata);
    const storedId = getCurrentSessionId();
    const valid = storedId && metadata.some(meta => meta.id === storedId) ? storedId : null;
    if (!valid) {
      createNewSession(metadata);
      return;
    }
    setActiveSessionId(valid);
    const data = loadSession(valid);
    if (data?.messages) setMessages(data.messages);
    if (data?.threadId !== undefined) setThreadId(data.threadId);
  }, [active, createNewSession, setMessages, setThreadId]);

  // Auto-save: persist current session whenever messages or threadId change
  useEffect(() => {
    if (!active || !activeSessionId) return;
    const now = new Date().toISOString();
    const existing = loadSession(activeSessionId);
    const createdAt = existing?.createdAt || now;
    const title = deriveSessionTitle(messages);
    const preview = deriveSessionPreview(messages);
    const storedMessages = sanitizeMessagesForStorage(messages);
    const sessionData = {
      id: activeSessionId,
      title,
      createdAt,
      lastModified: now,
      messages: storedMessages,
      threadId: threadId ?? null,
    };
    saveSession(sessionData);

    const metadata: SessionMetadata = {
      id: activeSessionId,
      title,
      createdAt,
      lastModified: now,
      messageCount: messages.length,
      preview,
    };
    const merged = [
      metadata,
      ...loadSessionMetadata().filter(meta => meta.id !== activeSessionId),
    ].sort((a, b) => b.lastModified.localeCompare(a.lastModified));
    const { list, removed } = applySessionLimit(merged);
    saveSessionMetadata(list);
    removed.forEach(deleteSession);
    setSessions(list);
    setCurrentSessionId(activeSessionId);
  }, [activeSessionId, messages, threadId, active, applySessionLimit]);

  return {
    sessionsOpen,
    setSessionsOpen,
    sessions,
    activeSessionId,
    createNewSession,
    selectSession,
    removeSession,
  };
}
